import { Immutable } from "immer";
import React from "react";

import type { CompareMediaSection as Value } from "@reactivated";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { originalUrl } from "../../utils/wagtail";

import bannerStyles from "../../styles/composable/banner.module.scss";
import styles from "./index.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

// Constrain the handle to its container and mirror its horizontal position onto
// the resize overlay's width (both as a percentage of the container).
const animateDraggedHandle = (
    event: MouseEvent,
    xPosition: number,
    dragWidth: number,
    minLeft: number,
    maxLeft: number,
    containerOffset: number,
    containerWidth: number,
    dragElement: HTMLElement,
    resizeElement: HTMLElement,
): void => {
    let leftValue = event.pageX + xPosition - dragWidth;
    if (leftValue < minLeft) {
        leftValue = minLeft;
    } else if (leftValue > maxLeft) {
        leftValue = maxLeft;
    }
    const widthValue = `${
        ((leftValue + dragWidth / 2 - containerOffset) * 100) / containerWidth
    }%`;
    dragElement.style.left = widthValue;
    resizeElement.style.width = widthValue;
};

/**
 * The desktop image container is a drag-to-compare slider: dragging the handle
 * reveals the resize overlay. The pointer wiring is bound in an effect against
 * refs and torn down on unmount; `is-visible` is added on mount
 * (as the legacy progressive-enhancement did) so the slider only activates once
 * JS runs. The `left`/`width` mutations are transient drag state React does not
 * own.
 *
 * - ``image_2.alt`` -> serialized ``default_alt_text``.
 * - ``description`` is the serialized RichTextBlock string (includes the
 *   ``<div class="rich-text">`` wrapper).
 */
export const CompareMediaSection = (props: IProps): React.JSX.Element => {
    const containerRef = React.useRef<HTMLElement>(null);
    const dragRef = React.useRef<HTMLDivElement>(null);
    const resizeRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const container = containerRef.current;
        const window = getWindow();
        if (!container || !window) {
            return;
        }
        container.classList.add("is-visible");
        const dragElement = dragRef.current;
        const resizeElement = resizeRef.current;
        if (!dragElement || !resizeElement) {
            return;
        }

        const raf = (fn: () => void): void => {
            window.requestAnimationFrame(fn);
        };

        let onMouseMove = (_moveEvent: MouseEvent): void => {};

        const onMouseDown = (event: MouseEvent): void => {
            event.preventDefault();
            dragElement.classList.add("draggable");
            resizeElement.classList.add("resizable");

            const document = getDocument();
            const dragWidth = dragElement.offsetWidth;
            const xPosition =
                dragElement.getBoundingClientRect().left +
                (document?.body.scrollLeft || 0) +
                dragWidth -
                event.pageX;
            const containerOffset =
                container.getBoundingClientRect().left +
                (document?.body.scrollLeft || 0);
            const containerWidth = container.offsetWidth;
            const minLeft = containerOffset + 10;
            const maxLeft = containerOffset + containerWidth - dragWidth - 10;

            onMouseMove = (moveEvent: MouseEvent): void => {
                raf(() => {
                    animateDraggedHandle(
                        moveEvent,
                        xPosition,
                        dragWidth,
                        minLeft,
                        maxLeft,
                        containerOffset,
                        containerWidth,
                        dragElement,
                        resizeElement,
                    );
                });
            };
            getDocument()?.body.addEventListener("mousemove", onMouseMove);
        };

        const onMouseUp = (): void => {
            dragElement.classList.remove("draggable");
            resizeElement.classList.remove("resizable");
            getDocument()?.body.removeEventListener("mousemove", onMouseMove);
        };

        dragElement.addEventListener("mousedown", onMouseDown);
        dragElement.addEventListener("mouseup", onMouseUp);
        getDocument()?.body.addEventListener("mouseup", onMouseUp);

        return () => {
            dragElement.removeEventListener("mousedown", onMouseDown);
            dragElement.removeEventListener("mouseup", onMouseUp);
            const document = getDocument();
            document?.body.removeEventListener("mouseup", onMouseUp);
            document?.body.removeEventListener("mousemove", onMouseMove);
        };
    }, []);

    return (
        <section
            className={concatClassNames([
                bannerStyles.banner,
                "compare",
                props.value.orientation === "right"
                    ? bannerStyles.bannerReverse
                    : null,
            ])}
        >
            <figure
                className={concatClassNames([
                    bannerStyles.bannerFigure,
                    "mobile-only",
                ])}
            >
                <img
                    aria-hidden="true"
                    className="img-responsive"
                    src={originalUrl(props.value.mobile_image)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
            </figure>

            <figure
                ref={containerRef}
                className={concatClassNames([
                    bannerStyles.bannerFigure,
                    styles.cdImageContainer,
                    "desktop-only",
                ])}
            >
                <img
                    aria-hidden="true"
                    className="img-responsive"
                    src={originalUrl(props.value.image)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />

                <div ref={resizeRef} className={styles.cdResizeImg}>
                    <img
                        aria-hidden="true"
                        src={originalUrl(props.value.image_2)}
                        alt={props.value.image_2?.default_alt_text ?? ""}
                        loading="lazy"
                        decoding="async"
                    />
                </div>

                <div ref={dragRef} className={styles.cdHandle}>
                    <svg>
                        <title>Image Slider Reveal Icon</title>
                        <use xlinkHref="#icon-slide-handle" />
                    </svg>
                </div>
            </figure>

            <div className={bannerStyles.bannerContent}>
                <h5 className={bannerStyles.bannerTitle}>
                    {props.value.title}
                </h5>
                <div
                    className={concatClassNames([
                        bannerStyles.bannerCopy,
                        "alt-copy",
                    ])}
                    dangerouslySetInnerHTML={{
                        __html: props.value.description ?? "",
                    }}
                />
            </div>
        </section>
    );
};
